import {TDateISO} from './dates';
import {ContentSources, EmotionsType, LangCodeType, OptionalMerge, SentimentType} from './shared';
import type {Platform} from 'hsi/utils/platform';

export type MentionEmotionClassification = {
    classifierId: 'emotions';
    confidence: number;
    name: Capitalize<EmotionsType>;
    labelId: Capitalize<EmotionsType>;
};

export type MentionClassification = MentionEmotionClassification; //Can add other types o classification here if required

export type MentionImageInfoType = {
    url: string;
    imageDimensions: {
        width: number;
        height: number;
    };
};

type BaseMentionType = {
    id: string; //saved search does not have an id. We normalise by using resourceId
    date: TDateISO; //technically isn't a TDateISO, but this typing is good enough (TDateISO type can't acurately captuire all possible ISO dates)
    url: string;
    domain: string;
    author: string; //could be empty string
    fullname: string | null;
    avatarUrl: string | null;
    title: string | null;
    snippet: string;
    language: LangCodeType;
    matchPositions?: {start: number; text: string; length: number}[];

    threadAuthor: string | null;

    city: string | null;
    cityCode: string | null;
    region: string | null;
    regionCode: string | null;
    country: string | null;
    countryCode: string | null;
    continent: string | null;
    continentCode: string | null;

    mediaUrls: string[] | null;
    contentSource: Platform;
    hasBeenTracked?: boolean | null; // to mark if mention was tracked for NLA/CLA purpose per session, avoid tracking the same mention multiple times with reload/rerenders
    pubType: string,
};

export interface QuickSearchMentionType extends BaseMentionType {
    apiType: 'quick';

    logoIds: any[]; //Not actually sure what the type of these is, but we aren't currently using them, so don't need it
    objectIds: any[]; //as above

    imageInfo: MentionImageInfoType[] | null;

    authorFollowers: number;
    authorFollowing: number;
}

export type QuickSearchMentionApiType = Omit<QuickSearchMentionType, 'apiType'>;

export interface SavedSearchMentionType extends BaseMentionType {
    apiType: 'saved';
    resourceId: string;
    queryId: number;
    sentiment: SentimentType;

    subtype: null | 'photo' | 'video' | 'other' | 'question' | 'link' | 'status';

    site?: string; //Is this optional?
    fullText?: string; //Will only appear if the mention was requested with the fulltext option, not sure if I need to include that in the type?
    pageType: ContentSources;
    pageTypeName: string; //human-readable version of pageType
    classifications?: MentionClassification[];
    tags: string[];

    reachEstimate: number;
    impressions: number;

    redditScore: number | null;

    facebookComments: number;
    facebookLikes: number;

    instagramInteractionsCount: number;
    instagramLikeCount: number;
    instagramCommentCount: number;

    twitterRetweets: number;
    twitterPostCount: number;
    twitterFollowers: number;
    twitterFollowing: number;
    twitterReplyCount: number;

    locationName: string | null;

    imageInfo?: MentionImageInfoType[];

    guid?: string; //I'm not sure if this is actually optional

    //TODO better typing
    imageMd5s?: string[];
    entityInfo?: BCRAPIEntity[];
}

export type BCRAPIEntity = {
    entityId: number;
    entityConfidence: string; //'HIGH'?
    url: string;
};

export type SavedSearchMentionApiType = Omit<SavedSearchMentionType, 'apiType' | 'id'>;

export type MentionType = QuickSearchMentionType | SavedSearchMentionType;

export function isSavedSearchMention(mention?: MentionType): mention is SavedSearchMentionType {
    return mention?.apiType === 'saved';
}

/**
 * Saved search only
 */
export type MentionAPIIdentifier = Pick<SavedSearchMentionType, 'resourceId' | 'queryId' | 'date'>;

//I have no idea why we capitalise the emotion names here, but we do.
export type MentionEmotionsType = Record<Capitalize<EmotionsType>, number>;

export type MentionDataImageType = {
    url: string;
    width?: number;
    height?: number;
    alt: string;
};

/**
 * This type would ideally not be needed, but is used by the normalizeMention method, because
 * it was written in a way that does not lend itself to the 'proper' approach, and is far
 * too difficult to follow to refactor safely. This type should probably not be used anywhere else
 */
export type AnyMentionType = OptionalMerge<SavedSearchMentionType, QuickSearchMentionType>;

export interface MentionDataType
    extends Pick<
        AnyMentionType,
        | 'fullname'
        | 'reachEstimate'
        | 'sentiment'
        | 'date'
        | 'snippet'
        | 'fullText'
        | 'url'
        | 'tags'
        | 'twitterRetweets'
        | 'facebookComments'
        | 'facebookLikes'
        | 'redditScore'
        | 'instagramInteractionsCount'
    > {
    id: number | string;
    title: string | null;
    site: string;
    username: string | null;
    avatar: MentionType['avatarUrl'];
    domain: string;

    emotion: EmotionsType[] | null;
    emotions: MentionEmotionsType | null;
    language: string | null;
    location: string | null;
    contentCategory: string | undefined;
    hasAuthor: boolean;
    hasMetaData: boolean;
    authorUrl: string | null;
    image: MentionDataImageType | null;
    video: string | null;
    pageTypeName: string | null;

    //metrics
    twitterImpressions: AnyMentionType['impressions'];
}

//EXAMPLE SAVED SEARCH MENTION
// {
//     "accountType": null,
//     "added": "2022-11-30T08:13:18.764+0000",
//     "assignment": null,
//     "author": "iizhiohba (在不平中找平衡)",
//     "avatarUrl": null,
//     "blogName": null,
//     "broadcastMediaUrl": null,
//     "isSyndicated": false,
//     "airType": null,
//     "broadcastType": null,
//     "mediaType": null,
//     "categories": [],
//     "categoryDetails": [],
//     "checked": false,
//     "city": null,
//     "cityCode": null,
//     "continent": "Asia",
//     "continentCode": "ASIA",
//     "country": "Taiwan",
//     "countryCode": "TWN",
//     "region": null,
//     "regionCode": null,
//     "date": "2022-11-30T22:44:00.000+0000",
//     "displayUrls": [],
//     "domain": "ptt.cc",
//     "engagementType": null,
//     "expandedUrls": [],
//     "facebookAuthorId": null,
//     "facebookComments": 0,
//     "facebookLikes": 0,
//     "facebookRole": null,
//     "facebookShares": 0,
//     "facebookSubtype": null,
//     "fullText": "**準備舊換新、可討論換五門車、驗車期中簡易看車全款價** **個人覺得雙北算常見還在路上的同款美車，借PTT人氣、找找有緣人** 車輛品牌/產地/出廠年月： BMW/德國/南部代理/1997.7 車款型式/排氣量： BMW E38 728iA/2793c.c. 顏色/排檔形式： 黑/自排/金屬天窗 有無調表/實際里程： 個人無調表需求及能力、從內外車況判斷前手里程應可信； 目前114,870KM，請參考但必然會增加 欲售售價： TWD 120,000 整 含餘下強制險 & 監理單位方面所有規費；入帳即交車 原使用情形： 個人為第三手、均自用； 因緣際會下入手，每次觀車及駕駛都心情愉悅； 來回中部、東部和南部高速和一般道路均無問題。 未來可能至國外工作，擬售出或交換適合實用車款給外縣市家人使用，讓有心者接手 交易地區/聯絡方式： 人/車在台北、站內信討論，約時間看車； 首重誠信及互動感覺，婉謝談不來者 實際照片： https://imgur.com/bFOLGoV.jpg https://imgur.com/wcrgq8F.jpg https://imgur.com/5WYQnqA.jpg https://imgur.com/VMScFUy.jpg https://imgur.com/M1VvpT7.jpg https://imgur.com/O5CEAZE.jpg https://imgur.com/9RBd9U8.jpg 三個值得買、和三個需要考慮的理由： ‧里程低，車況和外觀有水準 ‧經典車型，有讓你回頭 ‧豪華車格，空間大 & 穩定舒適 ‧25歲的紳士(或說青年？) ‧維護上精力和經濟基礎 ‧行駛和停放需要更多空間 現況交車，眼見為憑： 0. 入手後輪胎/底盤/引擎/變速箱/油/水/電皆進行檢查及依技師建議作相應維護/更換， 目前全車為DUNLOP VE303；均停放室內，前手有鍍膜 1. 因更換傳統避震/倍適登黑桶，後座C柱附近天篷因此施工有部份脫落及瑕疵 2. 六月於汐止耐迪補過冷媒觀察中，後續可與店家配合抓漏，個人原先不打算拆解老車 3. 電動遮陽簾偶爾接觸不良，因少載人及貼隔熱紙後未再啟用 4. 後座杯架開啟可能需要一定技巧及熟練；前後杯架均正常、但未使用過 5. 特定表面及黏膠為環保材質，因面積小、個人目前無視 & 不使用 6. 隨車交付，依最終/實際為主： 6.0 遙控鑰匙(全正常/無法發動但可車門解鎖 各一)、Valet Key 6.1 配件：TAPE轉3.5mm音源線卡帶、前座杯架、頭枕掛勾 6.2 零件/待用品：MANN機油濾心、FUCHS TITAN 5W40 GT1機油 & PSF方向機油、 BMW水箱精 6.3 整理記錄 & 各式文件 & 迄今電子影像檔，不備載 7. 目前先嘗試帶著車籍出售；另一選項是轉手給玩家當備車、個人搭配舊換新； 希望能優先延續生命。 **個人覺得雙北算常見還在路上的同款美車，借PTT人氣、找找有緣人** --",
//     "fullname": "iizhiohba (在不平中找平衡)",
//     "gender": "unknown",
//     "impressions": 0,
//     "insightsHashtag": [],
//     "insightsMentioned": [],
//     "instagramCommentCount": 0,
//     "instagramFollowerCount": 0,
//     "instagramFollowingCount": 0,
//     "instagramInteractionsCount": 0,
//     "instagramLikeCount": 0,
//     "instagramPostCount": 0,
//     "interest": [],
//     "itemReview": null,
//     "language": "zh-Hant",
//     "lastAssignmentDate": null,
//     "latitude": 0,
//     "linkedinComments": 0,
//     "linkedinEngagement": 0,
//     "linkedinImpressions": 0,
//     "linkedinLikes": 0,
//     "linkedinShares": 0,
//     "linkedinSponsored": false,
//     "linkedinVideoViews": 0,
//     "locationName": "TWN",
//     "longitude": 0,
//     "matchPositions": [
//         {
//             "start": 80,
//             "text": "BMW",
//             "length": 3
//         },
//         {
//             "start": 109,
//             "text": "BMW",
//             "length": 3
//         }
//     ],
//     "mediaFilter": null,
//     "mediaUrls": [],
//     "monthlyVisitors": 0,
//     "originalUrl": "https://www.ptt.cc/bbs/CarShop/M.1669790649.A.B43.html#6654ad912cb637e5853b6126b62d07b3",
//     "pageType": "forum",
//     "parentPostId": null,
//     "parentBlogName": null,
//     "priority": null,
//     "professions": [],
//     "pubType": "TOPIC",
//     "publisherSubType": null,
//     "queryId": 163685573,
//     "queryName": "sadadasda",
//     "rating": null,
//     "reachEstimate": null,
//     "redditScore": null,
//     "redditScoreUpvoteRatio": null,
//     "redditComments": null,
//     "redditAuthorKarma": null,
//     "redditAuthorAwardeeKarma": null,
//     "redditAuthorAwarderKarma": null,
//     "replyTo": null,
//     "resourceType": "crimsonMonitor",
//     "retweetOf": null,
//     "rootPostId": null,
//     "rootBlogName": null,
//     "sentiment": "neutral",
//     "shortUrls": [],
//     "snippet": "**準備舊換新、可討論換五門車、驗車期中簡易看車全款價** **個人覺得雙北算常見還在路上的同款美車，借PTT人氣、找找有緣人** 車輛品牌/產地/出廠年月： BMW/德國/南部代理/1997.7 車款型式/排氣量： BMW E38 728iA/2793c.c. 顏色/排檔形式： 黑/自排/金屬天窗 有無調表/實際里程： 個人無調表需求及能力、從內外車況判斷前手里程應可信； 目前114,870KM，請參考但必然會增加 欲售售價： TWD 120,000 整 含餘下強制險 & 監理單位方面所有規費；入帳即交車 原使用情形： 個人為第三手、均自用； 因緣際會下入手，每次觀車及駕駛都心情愉悅； 來回中部、東部和南部高速和一般道路均無問題。 未來可能至國外工作，擬售出或交換適合實用車款給外縣市家...",
//     "starred": false,
//     "status": null,
//     "subreddit": null,
//     "subredditSubscribers": null,
//     "subtype": null,
//     "tags": [],
//     "threadAuthor": null,
//     "threadCreated": null,
//     "threadEntryType": "post",
//     "threadId": "0",
//     "threadURL": null,
//     "title": "[售車] 1997 BMW E38 728iA 黑 可換五門車",
//     "twitterAuthorId": null,
//     "twitterFollowers": 0,
//     "twitterFollowing": 0,
//     "twitterPostCount": 0,
//     "twitterReplyCount": 0,
//     "twitterRetweets": 0,
//     "twitterLikeCount": null,
//     "twitterRole": null,
//     "twitterVerified": false,
//     "updated": "2022-11-30T08:13:18.764+0000",
//     "url": "https://www.ptt.cc/bbs/CarShop/M.1669790649.A.B43.html#6654ad912cb637e5853b6126b62d07b3",
//     "copyright": [],
//     "weblogTitle": "iizhiohba (在不平中找平衡)",
//     "pageTypeName": "Forums",
//     "contentSource": "forum",
//     "contentSourceName": "Forums",
//     "impact": null,
//     "resourceId": "c67419eaaa5a4d7ef1818175ad01577f"
// }

//EXAMPLE NEW QUICKSEARCH MENTION:
// {
//     "id": "http://twitter.com/kitsunettai/statuses/1671429999360974850",
//     "date": "2023-06-21T08:09:00.000Z",
//     "url": "http://twitter.com/kitsunettai/statuses/1671429999360974850",
//     "title": null,
//     "language": "en",
//     "author": "kitsunettai",
//     "fullname": "irinoling",
//     "threadAuthor": "smolsungies",
//     "avatarUrl": "https://audiences.brandwatch.com/api/audiences/v1/avatars/twitter/1443606773915598853?token=f85893548783c03c33f0bc268f0131c005e4db28826cb3ae260412bf0e576c9a",
//     "snippet": "RT @smolsungies so cute han was holding 2 quokka dolls and leeknow asked what it is and shook one around then pointed to the other quokka doll and said smth to han (probs 'this looks like you')(teasingly) with a hand on his shoulder and han answered \"thanks\uD83D\uDE00 thanks\uD83D\uDE00\" https://t.co/uASthsvWu8",
//     "matchPositions": [
//         {
//             "start": 42,
//             "text": "quokka",
//             "length": 6
//         },
//         {
//             "start": 131,
//             "text": "quokka",
//             "length": 6
//         }
//     ],
//     "domain": "twitter.com",

//     "contentSource": "twitter",//?
//     "contentSourceName": "TWITTER",//?

//     "cityCode": null,
//     "city": null,
//     "regionCode": null,
//     "region": null,
//     "continentCode": null,
//     "continent": null,
//     "countryCode": null,
//     "country": null,

//     "logoIds": [],
//     "objectIds": [],
//     "imageInfos": null,
//     "authorFollowers": 3,
//     "authorFollowing": 120
// },
